import { useMemo, useState } from 'react'

import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { NoticeMarkdown } from '@/components/notices/notice-markdown'
import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useSaveNotice, type Notice, type NoticeWrite } from '@/hooks/use-notices'

const AUDIENCE_OPTIONS = [
  { value: 'candidate', label: 'Talent' },
  { value: 'employer', label: 'Employer' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'interviewer', label: 'Interviewer' },
  { value: 'platform', label: 'Platform' },
] as const

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

function defaultStart(): string {
  return toLocalInput(new Date().toISOString())
}

export function NoticeCanvasForm({
  notice,
  onClose,
}: {
  notice: Notice | null
  onClose: () => void
}) {
  const saveNotice = useSaveNotice()
  const initial = useMemo(
    () => ({
      body: notice?.body ?? '',
      audiences: notice?.audiences ?? [],
      start: notice ? toLocalInput(notice.starts_at) : defaultStart(),
      end: toLocalInput(notice?.ends_at),
      dismissible: notice?.dismissible ?? true,
      alsoNotify: notice?.also_notify ?? false,
    }),
    [notice],
  )
  const [body, setBody] = useState(initial.body)
  const [audiences, setAudiences] = useState<string[]>(initial.audiences)
  const [start, setStart] = useState(initial.start)
  const [end, setEnd] = useState(initial.end)
  const [dismissible, setDismissible] = useState(initial.dismissible)
  const [alsoNotify, setAlsoNotify] = useState(initial.alsoNotify)
  const [error, setError] = useState<string | null>(null)

  const isDirty =
    body !== initial.body ||
    start !== initial.start ||
    end !== initial.end ||
    dismissible !== initial.dismissible ||
    alsoNotify !== initial.alsoNotify ||
    audiences.join(',') !== initial.audiences.join(',')

  const toggleAudience = (value: string, checked: boolean) => {
    setAudiences((current) => {
      if (checked) return current.includes(value) ? current : [...current, value]
      return current.filter((item) => item !== value)
    })
  }

  const onSave = () => {
    const text = body.trim()
    if (!text) {
      setError('Write the notice.')
      return
    }
    const startsAt = fromLocalInput(start)
    if (!startsAt) {
      setError('Choose when it starts.')
      return
    }
    const endsAt = fromLocalInput(end)
    if (end && !endsAt) {
      setError('Choose a valid end.')
      return
    }
    if (endsAt && new Date(endsAt) <= new Date(startsAt)) {
      setError('End must be after the start.')
      return
    }
    const payload: NoticeWrite = {
      body: text,
      audiences,
      starts_at: startsAt,
      ends_at: endsAt,
      dismissible,
      also_notify: alsoNotify,
    }
    setError(null)
    saveNotice.mutate(
      { id: notice?.id, payload },
      {
        onSuccess: () => onClose(),
        onError: (saveError: Error) => setError(saveError.message || 'Could not save the notice.'),
      },
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <FormField
          label="Notice"
          htmlFor="notice-body"
          description="Shown in the sidebar; a heading, short paragraphs, and bold are fine; headings stay small."
        >
          <Textarea
            id="notice-body"
            value={body}
            rows={5}
            maxLength={500}
            onChange={(event) => setBody(event.target.value)}
          />
        </FormField>
        {body.trim() ? (
          <div className="rounded-md border border-border bg-card px-3 py-2 text-card-foreground">
            <NoticeMarkdown>{body}</NoticeMarkdown>
          </div>
        ) : null}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Who it's for</legend>
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="notice-audience-everyone"
              aria-labelledby="notice-audience-everyone-label"
              checked={audiences.length === 0}
              onCheckedChange={(checked) => {
                if (checked) setAudiences([])
              }}
            />
            <span id="notice-audience-everyone-label">Everyone</span>
          </div>
          {AUDIENCE_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center gap-2 text-sm">
              <Checkbox
                id={`notice-audience-${option.value}`}
                aria-labelledby={`notice-audience-${option.value}-label`}
                checked={audiences.includes(option.value)}
                onCheckedChange={(checked) => toggleAudience(option.value, checked === true)}
              />
              <span id={`notice-audience-${option.value}-label`}>{option.label}</span>
            </div>
          ))}
        </fieldset>
        <FormField label="Starts" htmlFor="notice-start">
          <Input
            id="notice-start"
            type="datetime-local"
            value={start}
            onChange={(event) => setStart(event.target.value)}
          />
        </FormField>
        <FormField label="Ends" htmlFor="notice-end" description="Leave empty to keep it on.">
          <Input
            id="notice-end"
            type="datetime-local"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
          />
        </FormField>
        <FormField label="People can dismiss this" htmlFor="notice-dismissible">
          <Switch
            id="notice-dismissible"
            checked={dismissible}
            onCheckedChange={setDismissible}
          />
        </FormField>
        <FormField
          label="Also notify"
          htmlFor="notice-notify"
          description="Send an inbox notification when this starts. People can mute it."
        >
          <Switch id="notice-notify" checked={alsoNotify} onCheckedChange={setAlsoNotify} />
        </FormField>
      </div>
      <ArtifactCanvasInlineError>{error}</ArtifactCanvasInlineError>
      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={onSave}
            isDirty={isDirty}
            isSaving={saveNotice.isPending}
            closeLabel="Dismiss"
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
